import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { generateLeaderActionPlan, mockLeaderInput } from '../data/leaderActionLogic';

export const LeaderActionEngine = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('30day');

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setPlan(generateLeaderActionPlan(mockLeaderInput));
      setIsGenerating(false);
    }, 2000);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">AI Leader Action Engine</h1>
          <p className="text-slate-500 mt-1">Personlig handlingsplan for operativ ledelse.</p>
        </div>
        {plan && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={<SafeIcon name="Download" />}>Eksporter PDF</Button>
            <Button variant="primary" icon={<SafeIcon name="Share2" />}>Send til HR-Partner</Button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!plan ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
             <Card className="bg-slate-900 border-none text-white overflow-hidden relative shadow-2xl">
               <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] -mr-20 -mt-20" />
               <CardContent className="p-16 text-center space-y-8 relative z-10">
                 <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto backdrop-blur-md border border-white/10">
                   <SafeIcon name="Zap" className="text-4xl text-indigo-400" />
                 </div>
                 <div className="space-y-4">
                    <h2 className="text-3xl font-black uppercase tracking-tighter leading-tight">Konkretiser ditt lederskap</h2>
                    <p className="text-indigo-100/60 max-w-md mx-auto">
                      AI-motoren analyserer ditt teams unike utfordringer og designer en 90-dagers plan som balanserer drift og psykososial støtte.
                    </p>
                 </div>
                 <Button size="lg" onClick={handleGenerate} disabled={isGenerating} className="px-12 py-6 text-lg font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-500 shadow-xl shadow-indigo-500/20">
                    {isGenerating ? "Designer handlingsplan..." : "Generer Lederplan"}
                 </Button>
               </CardContent>
             </Card>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Summary & Priorities */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-indigo-50 border-indigo-100 border-l-4 border-l-indigo-600">
                <CardHeader title="Situasjonsanalyse" subtitle={`Leder: ${plan.meta.leader} • Enhet: ${plan.meta.dept}`} icon={<SafeIcon name="FileText" className="text-indigo-600" />} />
                <CardContent>
                  <p className="text-lg text-indigo-900 leading-relaxed font-medium italic">"{plan.meta.summary}"</p>
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {plan.priorities.map((p, i) => (
                      <div key={i} className="p-4 bg-white rounded-2xl border border-indigo-100 shadow-sm">
                        <p className="text-[9px] font-black text-indigo-500 uppercase mb-2">Prioritet 0{i+1}</p>
                        <p className="text-xs font-bold text-slate-800 leading-tight">{p}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Timeline Navigation */}
              <div className="flex gap-4 p-1 bg-slate-100 rounded-[20px] w-fit">
                {['30day', '60day', '90day'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-8 py-3 rounded-[18px] text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {tab === '30day' ? 'Fase 1: 30 dager' : tab === '60day' ? 'Fase 2: 60 dager' : 'Fase 3: 90 dager'}
                  </button>
                ))}
              </div>

              {/* Active Actions */}
              <div className="space-y-4">
                {plan.timeline[activeTab === '30day' ? 'day30' : activeTab === '60day' ? 'day60' : 'day90'].map((action, i) => (
                  <Card key={i} className="border-none shadow-lg overflow-hidden group">
                    <CardContent className="p-0 flex">
                      <div className="w-2 bg-indigo-600" />
                      <div className="p-8 flex-1 flex justify-between items-center">
                        <div className="space-y-2">
                          <h4 className="text-xl font-black text-slate-900 uppercase tracking-tight">{action.title}</h4>
                          <p className="text-slate-500 font-medium leading-relaxed max-w-xl">{action.desc}</p>
                        </div>
                        <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-all">
                          <SafeIcon name="CheckCircle" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Communication Script */}
              <Card className="bg-slate-900 border-none text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16" />
                <CardHeader title="Kommunikasjons-støtte" subtitle="Manus for neste teammøte" icon={<SafeIcon name="MessageSquare" className="text-indigo-400" />} />
                <CardContent className="space-y-6 relative z-10">
                  <div className="p-6 bg-white/5 rounded-3xl border border-white/10 italic text-indigo-100 font-serif leading-relaxed">
                    "{plan.communication.script}"
                  </div>
                  <div className="space-y-3">
                    <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Viktige budskap</p>
                    {plan.communication.points.map((pt, i) => (
                      <div key={i} className="flex gap-3 text-xs text-slate-300 items-start">
                        <SafeIcon name="Check" className="text-indigo-500 shrink-0 mt-0.5" />
                        <span>{pt}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader title="Oppfølgings-kadens" icon={<SafeIcon name="Clock" />} />
                <CardContent>
                  <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-sm font-black text-slate-900 leading-snug">{plan.cadence}</p>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-4 leading-relaxed font-medium uppercase tracking-widest italic">
                    *Anbefalt frekvens basert på nåværende stress-score og trygghetsnivå.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-xl">
                 <CardHeader title="Lederens Sjekkliste" subtitle="Gjøremål for denne måneden" />
                 <CardContent className="space-y-4">
                    {plan.checklist.map((item, i) => (
                      <div key={i} className="flex gap-4 items-center group cursor-pointer">
                        <div className="w-6 h-6 rounded-lg border-2 border-slate-200 flex items-center justify-center group-hover:border-indigo-500 transition-colors">
                          <div className="w-2 h-2 bg-indigo-500 rounded-sm opacity-0 group-hover:opacity-100 scale-0 group-hover:scale-100 transition-all" />
                        </div>
                        <span className="text-xs font-bold text-slate-600 group-hover:text-slate-900 transition-colors">{item}</span>
                      </div>
                    ))}
                 </CardContent>
              </Card>

              <div className="bg-emerald-600 p-8 rounded-[40px] text-white shadow-xl shadow-emerald-100 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
                <SafeIcon name="TrendingUp" className="text-4xl text-emerald-200 mb-6" />
                <h4 className="text-xl font-black mb-2 uppercase tracking-tighter">Prognose</h4>
                <p className="text-xs text-emerald-100 leading-relaxed mb-6">
                  Ved å gjennomføre disse tiltakene estimerer vi en <b>12% økning i Leadership Score</b> og en merkbar reduksjon i opplevd stress innen neste kvartal.
                </p>
                <Button variant="secondary" className="w-full bg-white text-emerald-600 border-none font-black text-[10px] uppercase py-4">Se Effekt-simulering</Button>
              </div>

              <Card className="bg-slate-50 border-slate-200">
                <CardHeader title="Trenger du støtte?" icon={<SafeIcon name="Award" />} />
                <CardContent className="space-y-4">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Dette er en krevende plan å gjennomføre alene. Vi anbefaler 3 coaching-sesjoner for å trene på manus og prioritering.
                  </p>
                  <Button variant="primary" className="w-full bg-slate-900 hover:bg-slate-800 border-none text-[10px] uppercase font-black py-3">Book Ledger-Coach</Button>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};